import type {FormController} from './types';
import type {Validator} from 'svelte-forms';
import type {Observable} from 'rxjs';
import {BehaviorSubject, combineLatest} from 'rxjs';
import {CompositeFormController} from './composite-form-controller';
import type {Modifier} from './types';


export class FormGroupBuilder<T extends Record<keyof T, unknown>> {
    private formControllers: FormController<T[keyof T]>[] = [];
    private validators: Validator[] = [];
    private modifiers: Modifier[] = [];

    public registerFormControl(formController: FormController<T[keyof T]>): void {
        this.formControllers.push(formController);
    }

    public addValidators(validators: Validator[]): FormGroupBuilder<T> {
        this.validators = [...this.validators, ...validators];
        return this;
    }

    public addModifiers(modifiers: Modifier[]): FormGroupBuilder<T> {
        this.modifiers = [...this.modifiers, ...modifiers];
        return this;
    }

    public getLastRegisteredFormController(): FormController<T[keyof T]> | null {
        if (this.formControllers.length === 0) {
            return null;
        }
        return this.formControllers[this.formControllers.length - 1];
    }

    public build(id: string, name: string, editorNameToFieldNameMap: Map<string, keyof T>): FormGroup<T> {
        return new FormGroup<T>(id, name, [...this.formControllers], [...this.validators], [...this.modifiers], editorNameToFieldNameMap);
    }
}

export class FormGroup<T extends Record<keyof T, unknown>> extends CompositeFormController<T, T[keyof T]> {

    constructor(id: string,
                name: string,
                formControllers: FormController<T[keyof T]>[],
                validators: Validator[],
                modifiers: Modifier[],
                private editorNameToFieldNameMap: Map<string, keyof T>) {
        super(id, name, formControllers, validators, modifiers);
        this.initializeController();
    }

    public addField(newFormController: FormController<T[keyof T]>, name: keyof T): void {
        this.editorNameToFieldNameMap.set(newFormController.name, name);
        this.addController(newFormController)
    }

    public setModelValue(newModelValue: T) {
        this.formControllers.forEach((value) => {
            if (newModelValue !== null) {
                value.setModelValue(newModelValue[this.editorNameToFieldNameMap.get(value.name)])
            }
        })
    }

    public removeField(toBeRemovedFieldName: string): void {
        this.removeControllerByName(toBeRemovedFieldName);
        this.editorNameToFieldNameMap.delete(toBeRemovedFieldName);
    }

    protected combineToModelValue$(formControllers: FormController<T[keyof T]>[]): Observable<T> {
        if (formControllers.length === 0) {
            return new BehaviorSubject({} as T).asObservable();
        }

        const entries = formControllers.map((controller) => ([this.editorNameToFieldNameMap.get(controller.name), controller.getModelValue$()] as const));
        return combineLatest(Object.fromEntries(entries) as {[KEY in keyof T]: Observable<T[KEY]>});
    }

    protected getModelFromStateValue(stateValue: Record<string, T[keyof T]>): T {
        const entries = this.formControllers.map((controller) => ([this.editorNameToFieldNameMap.get(controller.name), stateValue[controller.name]] as const));
        return Object.fromEntries(entries) as T; // cast because Object.fromEntries return only Record<string, T[keyof T]>
    }
}